import SEO from "@/components/SEO";
import JsonLd from "@/components/JsonLd";
import AdUnit from "@/components/AdUnit";
import HeroBanner from "@/components/HeroBanner";
import QuickLinks from "@/components/QuickLinks";
import FeaturedDestinations from "@/components/FeaturedDestinations";
import ExploreMap from "@/components/ExploreMap";
import TurkishCuisine from "@/components/TurkishCuisine";
import CulturalExperiences from "@/components/CulturalExperiences";
import TravelTips from "@/components/TravelTips";
import FeaturedBlogPosts from "@/components/FeaturedBlogPosts";
import Newsletter from "@/components/Newsletter";
import TravelOptions from "@/components/TravelOptions";
import CustomLink from "@/components/CustomLink";

export default function Home() {
  return (
    <>
      <SEO
        title="Turkey Travel Guide 2026: Destinations, Tips & Itineraries"
        description="Plan your perfect Turkey trip with our comprehensive 2026 guide. Explore Istanbul, Cappadocia, Antalya, Bodrum and 10+ destinations with costs, safety tips, visa info, and sample itineraries."
        canonicalUrl="/"
        keywords="Turkey travel guide 2026, Turkey vacation, holidays to Turkey, Istanbul travel guide, Cappadocia hot air balloon, Antalya beaches, Bodrum holidays, Turkey all inclusive, cheap holidays Turkey, Turkey visa requirements, Turkish food, is Turkey safe, Turkey itinerary, things to do in Turkey, Turkey tourism"
      />
      <JsonLd data={[
        {
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "Turkey Vacation Guide",
          "url": "https://turkeyvacationguide.com",
          "description": "Your comprehensive guide to exploring the wonders of Turkey.",
          "potentialAction": {
            "@type": "SearchAction",
            "target": "https://turkeyvacationguide.com/destinations?q={search_term_string}",
            "query-input": "required name=search_term_string"
          }
        },
        {
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Turkey Vacation Guide",
          "url": "https://turkeyvacationguide.com",
          "logo": {
            "@type": "ImageObject",
            "url": "https://turkeyvacationguide.com/og-image.jpg"
          },
          "areaServed": "Worldwide",
          "contactPoint": {
            "@type": "ContactPoint",
            "email": "info@turkeyvacationguide.com",
            "contactType": "customer service"
          }
        }
      ]} />
      
      <HeroBanner />
      <QuickLinks />
      <FeaturedDestinations />
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-6">Your Complete Turkey Travel Guide</h2>
            <div className="prose max-w-none text-gray-700">
              <p className="text-lg mb-4">
                Turkey sits at the crossroads of Europe and Asia, offering travelers an extraordinary blend of ancient history, stunning landscapes, and vibrant culture. From the bustling bazaars of <CustomLink href="/destinations/istanbul"><span className="text-[#E30A17] hover:underline">Istanbul</span></CustomLink> to the fairy chimneys of <CustomLink href="/destinations/cappadocia"><span className="text-[#E30A17] hover:underline">Cappadocia</span></CustomLink>, the white travertines of <CustomLink href="/destinations/pamukkale"><span className="text-[#E30A17] hover:underline">Pamukkale</span></CustomLink> to the turquoise beaches of <CustomLink href="/destinations/antalya"><span className="text-[#E30A17] hover:underline">Antalya</span></CustomLink>, every region offers something unique.
              </p>
              <p className="mb-4">
                Our guides cover everything you need to plan your trip: <CustomLink href="/destinations"><span className="text-[#E30A17] hover:underline">13 top destinations</span></CustomLink> with detailed itineraries, <CustomLink href="/travel-safety"><span className="text-[#E30A17] hover:underline">up-to-date safety information</span></CustomLink> and emergency numbers, <CustomLink href="/transportation"><span className="text-[#E30A17] hover:underline">transportation options</span></CustomLink> between cities, <CustomLink href="/accommodation"><span className="text-[#E30A17] hover:underline">accommodation recommendations</span></CustomLink> for every budget, and insider tips on <CustomLink href="/culture"><span className="text-[#E30A17] hover:underline">Turkish culture</span></CustomLink> and <CustomLink href="/cuisine"><span className="text-[#E30A17] hover:underline">cuisine</span></CustomLink>.
              </p>
              <p className="mb-4">
                Whether you're planning a week-long beach holiday on the Turkish Riviera, a cultural exploration of ancient ruins at <CustomLink href="/destinations/ephesus"><span className="text-[#E30A17] hover:underline">Ephesus</span></CustomLink>, or a hot air balloon ride over Cappadocia, our free travel resources help you make the most of your Turkish adventure. Check our <CustomLink href="/blog"><span className="text-[#E30A17] hover:underline">travel blog</span></CustomLink> for the latest guides and our <CustomLink href="/language-guide"><span className="text-[#E30A17] hover:underline">Turkish phrase guide</span></CustomLink> to connect with locals.
              </p>
            </div>
          </div>
        </div>
      </section>
      <TravelOptions />
      <ExploreMap />
      <TurkishCuisine />
      <CulturalExperiences />
      <TravelTips />
      <FeaturedBlogPosts />
      <AdUnit slot="home-horizontal" format="horizontal" className="container mx-auto px-4" />
      <Newsletter />
    </>
  );
}
